/**
 * Типы целей для открытия окон/ссылок
 */
export enum WindowTargets {
  /** Текущее окно/фрейм (по умолчанию) */
  SELF = '_self',

  /** Новая вкладка/окно (обычный новый таб) */
  BLANK = '_blank',

  /** Родительский фрейм (если находимся во фрейме) */
  PARENT = '_parent',

  /** Корневой фрейм (самое верхнее окно) */
  TOP = '_top',

  /** Новая вкладка без истории (Safari, Chrome) */
  BLANK_NO_OPENER = 'blank_noopener',

  /** Текущее окно с заменой истории */
  REPLACE = '_replace',
}

// Типы для TypeScript
export type WindowTargetType =
  | WindowTargets.SELF
  | WindowTargets.BLANK
  | WindowTargets.PARENT
  | WindowTargets.TOP
  | WindowTargets.BLANK_NO_OPENER
  | WindowTargets.REPLACE;

/**
 * Опции для открытия ссылки
 */
export interface IOpenLinkOptions {
  href: string;
  target?: WindowTargetType;
  windowFeatures?: string;
  rel?: string;
  title?: string;
  download?: boolean | string; // для скачивания файлов
  referrerPolicy?: ReferrerPolicy;
}

/**
 * Очистка ссылки после клика
 */
const cleanupLink = (link: HTMLAnchorElement): void => {
  requestAnimationFrame(() => {
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
  });
};

/**
 * Открытие в новом окне/вкладке
 */
const openInNewWindow = (
  href: string,
  target: WindowTargets.BLANK | WindowTargets.BLANK_NO_OPENER,
  features?: string,
  rel?: string,
): void => {
  const finalTarget = target === WindowTargets.BLANK_NO_OPENER ? '_blank' : target;

  const newWindow = window.open(href, finalTarget, features);

  // Безопасность: защита от reverse tabnabbing
  if (newWindow) {
    if (rel) {
      // Для современных браузеров
      newWindow.opener = null;
    }

    // Фокус на новом окне (если не попап)
    if (!features?.includes('popup')) {
      newWindow.focus();
    }
  }
};

/**
 * Создание элемента ссылки
 */
const createLinkElement = (options: {
  href: string;
  target: WindowTargets;
  download?: string;
  title?: string;
  referrerPolicy?: ReferrerPolicy;
}): HTMLAnchorElement => {
  const link = document.createElement('a');
  link.href = options.href;
  link.target = options.target;

  if (options.download !== undefined) {
    link.download = options.download;
  }

  if (options.title) {
    link.title = options.title;
  }

  if (options.referrerPolicy) {
    link.referrerPolicy = options.referrerPolicy;
  }

  // Скрываем элемент
  link.style.cssText = `
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  `;

  document.body.appendChild(link);
  return link;
};

/**
 * Открытие в текущем окне
 */
const openInCurrentWindow = (
  href: string,
  download?: boolean | string,
  title?: string,
  referrerPolicy?: ReferrerPolicy,
): void => {
  if (download) {
    // Скачивание файла
    const link = createLinkElement({
      href,
      target: WindowTargets.SELF,
      download: download === true ? '' : download,
      title,
      referrerPolicy,
    });
    link.click();
    cleanupLink(link);
  } else {
    // Обычная навигация
    window.location.href = href;
  }
};

/**
 * Открытие в родительском или верхнем фрейме
 */
const openInParentOrTop = (href: string, target: WindowTargets.PARENT | WindowTargets.TOP): void => {
  const targetWindow = target === WindowTargets.PARENT ? window.parent : window.top;

  if (targetWindow) {
    targetWindow.location.href = href;
  } else {
    // Fallback если нет родительского окна
    window.location.href = href;
  }
};

/**
 * Открытие в кастомной цели (фрейм, окно)
 */
const openInNamedTarget = (href: string, target: WindowTargets, features?: string): void => {
  const existingWindow = window.open('', target);

  if (existingWindow) {
    // Окно/фрейм уже существует
    existingWindow.location.href = href;
    existingWindow.focus();
  } else {
    // Создаем новое окно с именем
    window.open(href, target, features);
  }
};

/**
 * Резервный метод открытия
 */
const fallbackOpen = (href: string, target?: WindowTargets): void => {
  if (!target || target === WindowTargets.SELF || target === WindowTargets.REPLACE) {
    window.location.href = href;
  } else {
    window.open(href, target);
  }
};

/**
 * Вспомогательные функции для работы с целями
 */
export const WindowTargetsHelper = {
  /**
   * Проверка, является ли цель внешней (новая вкладка/окно)
   */
  isExternalTarget(target: WindowTargetType): boolean {
    return (
      target === WindowTargets.BLANK ||
      target === WindowTargets.BLANK_NO_OPENER ||
      (typeof target === 'string' && target.startsWith('_blank'))
    );
  },

  /**
   * Проверка, является ли цель фреймом
   */
  isFrameTarget(target: WindowTargetType): boolean {
    return (
      target === WindowTargets.PARENT ||
      target === WindowTargets.TOP ||
      (typeof target === 'string' && !target.startsWith('_'))
    );
  },

  /**
   * Получение безопасного значения rel для цели
   */
  getSafeRel(target: WindowTargetType): string {
    if (this.isExternalTarget(target)) {
      return 'noopener noreferrer nofollow';
    }
    return '';
  },

  /**
   * Создание параметров window.open для целевой аудитории
   */
  createWindowFeatures(options: {
    width?: number;
    height?: number;
    left?: number;
    top?: number;
    menubar?: boolean;
    toolbar?: boolean;
    location?: boolean;
    status?: boolean;
    resizable?: boolean;
    scrollbars?: boolean;
  }): string {
    const features: string[] = [];

    if (options.width) features.push(`width=${options.width}`);
    if (options.height) features.push(`height=${options.height}`);
    if (options.left) features.push(`left=${options.left}`);
    if (options.top) features.push(`top=${options.top}`);
    if (options.menubar !== undefined) features.push(`menubar=${options.menubar ? 'yes' : 'no'}`);
    if (options.toolbar !== undefined) features.push(`toolbar=${options.toolbar ? 'yes' : 'no'}`);
    if (options.location !== undefined) features.push(`location=${options.location ? 'yes' : 'no'}`);
    if (options.status !== undefined) features.push(`status=${options.status ? 'yes' : 'no'}`);
    if (options.resizable !== undefined) features.push(`resizable=${options.resizable ? 'yes' : 'no'}`);
    if (options.scrollbars !== undefined) features.push(`scrollbars=${options.scrollbars ? 'yes' : 'no'}`);

    return features.join(',');
  },
};

export const syntheticOpen = (options: IOpenLinkOptions): void => {
  const {
    href,
    target = WindowTargets.SELF,
    windowFeatures,
    rel = 'noopener noreferrer nofollow',
    title,
    download,
    referrerPolicy = 'strict-origin-when-cross-origin',
  } = options;

  try {
    if (!href || typeof href !== 'string') {
      throw new Error('Неверный URL');
    }

    switch (target) {
      case WindowTargets.BLANK:
      case WindowTargets.BLANK_NO_OPENER:
        openInNewWindow(href, target, windowFeatures, rel);
        break;

      case WindowTargets.REPLACE:
        window.location.replace(href);
        break;

      case WindowTargets.SELF:
        openInCurrentWindow(href, download, title, referrerPolicy);
        break;

      case WindowTargets.PARENT:
      case WindowTargets.TOP:
        openInParentOrTop(href, target);
        break;

      default:
        // Кастомное имя фрейма или окна
        openInNamedTarget(href, target, windowFeatures);
    }
  } catch (error) {
    console.error('Ошибка при открытии ссылки:', error);
    fallbackOpen(href, target);
  }
};
